import { MARKET_LOCALE, type Market } from '@/lib/market/types';

/**
 * =============================================================================
 *  as_of 時光機的原語（ARCHITECTURE §10.3）。
 *
 *  三件這裡必須做對、做錯了整條時光機就是裝飾品的事：
 *
 *  1. **日期一律是字串。** `"2015-06-30"`，不是 Date，不是 timestamp。
 *     §2.6 逐字：「把台股交易日用瀏覽器本地時區渲染是反覆出現的正確性 bug，
 *     date 字串直接消滅它。」所以這個模組**沒有任何一個函式回傳 Date**。
 *
 *  2. **「今天」是市場的今天，不是瀏覽器的今天。** 台北時間 09-17 08:00 時，
 *     紐約還在 09-16。用 `new Date().toISOString().slice(0,10)` 會在
 *     UTC+8 的凌晨把使用者送進未來，而未來的 as_of 是一個沒有意義的查詢。
 *
 *  3. **`as_of` 是不是過去，決定 staleTime 是不是 Infinity**（見 lib/api/stale.ts）。
 *     所以 `isPastAsOf` 的語意必須精確：**嚴格小於**市場今天。
 *     等於今天的 as_of 仍然會長出新資料（盤中、盤後、當日 DQ 修補），不能凍結。
 * ========================================================================== */

export const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** 專案的最早可查日。ROADMAP §4 的回補起點是 2005，但驗收 #2 查的是 2000 年。 */
export const MIN_AS_OF = '1990-01-01';

/**
 * 市場當地的「今天」，以 en-CA 取得，因為 en-CA 的短日期格式**就是** ISO 8601。
 * 這比手動拼 getFullYear/getMonth 少一個時區換算的機會犯錯。
 */
export function marketToday(market: Market, now: Date = new Date()): string {
  const { tz } = MARKET_LOCALE[market];
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** 只接受合法的 ISO 日期字串，其餘一律回 undefined（＝沒有倒帶，用今天）。 */
export function parseAsOf(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || !ISO_DATE_RE.test(raw)) return undefined;
  // 2015-02-30 通過了正則但不是一個日期。Date.parse 之後再轉回字串比對即可識破。
  const ms = Date.parse(`${raw}T00:00:00Z`);
  if (Number.isNaN(ms)) return undefined;
  return new Date(ms).toISOString().slice(0, 10) === raw ? raw : undefined;
}

/**
 * **嚴格**過去。等於今天 ≠ 過去——今天的資料還會變。
 * 這一行就是 §10.2「過去日期的視圖在構造上是不可變的」那句話的全部依據。
 */
export function isPastAsOf(asOf: string | undefined, today: string): boolean {
  return asOf !== undefined && asOf < today;
}

/** 未來的 as_of 沒有意義（也沒有資料）；夾回今天。過早的夾回 MIN_AS_OF。 */
export function clampAsOf(asOf: string, today: string): string {
  if (asOf > today) return today;
  if (asOf < MIN_AS_OF) return MIN_AS_OF;
  return asOf;
}

/** 位移天數。輸入輸出都是字串，中間那個 Date 不會外流。 */
export function shiftDays(iso: string, days: number): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

/** 兩個日期相差幾天（a - b）。用於「倒帶了 3,996 天」這種可讀的距離。 */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000);
}

/** 「2015-06-30（倒帶 4,096 天）」——倒帶距離要讓人一眼看到自己走多遠了。 */
export function describeRewind(asOf: string, today: string): string {
  const d = daysBetween(today, asOf);
  if (d <= 0) return '即時';
  if (d < 90) return `倒帶 ${String(d)} 天`;
  const years = d / 365.25;
  return years < 1 ? `倒帶 ${String(Math.round(d / 30.44))} 個月` : `倒帶 ${years.toFixed(1)} 年`;
}
